import {Component, OnDestroy, OnInit} from '@angular/core';
import {ToastrService} from 'ngx-toastr';
import {ImageTypeEnum, RoleType} from 'src/app/shared/graphql/globalTypes';
import {GetContent_content_edges_node} from '../../content/__generated__/GetContent';
import {BsModalRef, BsModalService} from 'ngx-bootstrap/modal';
import {ActivatedRoute, Router} from '@angular/router';
import {catchError, EMPTY, fromEvent, map, Subject, Subscription, switchMap} from 'rxjs';
import {TemplateEditMode} from '../../builder/builder-form/builder-form.component';
import {TemplateService} from 'src/app/shared/services/template.service';
import {TemplateService_GetTemplateItem_templateItem} from 'src/app/shared/services/__generated__/TemplateService_GetTemplateItem';
import {getUrl} from 'src/app/shared/utils/utils';
import {Apollo, gql} from 'apollo-angular';
import {ErrorService} from 'src/app/shared/services/error.service';
import {ContentDeleteComponent} from 'src/app/shared/modals/content-delete/content-delete.component';
import {
  TemplateDetails_GetContent,
  TemplateDetails_GetContentVariables,
} from './__generated__/TemplateDetails_GetContent';
import {PasswordProtectComponent} from 'src/app/shared/modals/password-protect/password-protect.component';
import {NavigationService} from 'src/app/shared/services/navigation.service';
import {TemplateTitleComponent} from 'src/app/shared/modals/template-title/template-title.component';
import {ArchiveTemplateComponent} from 'src/app/shared/modals/archive-template/archive-template.component';
import {MultiSelectComponentLabelFragment} from '../../../shared/components/multi-select/__generated__/MultiSelectComponentLabelFragment';
import {EditTemplateLabelsComponent} from '../../../shared/modals/edit-template-labels/edit-template-labels.component';
import {labelFrag, tagFrag} from '../../../shared/components/multi-select/multi-select.component';
import {EditTemplateTagsComponent} from '../../../shared/modals/edit-template-tags/edit-template-tags.component';
import {MultiSelectComponentTagFragment} from '../../../shared/components/multi-select/__generated__/MultiSelectComponentTagFragment';
import {
  TemplateDetailsComponent__getRole,
  TemplateDetailsComponent__getRole_me,
} from './__generated__/TemplateDetailsComponent__getRole';
import {TemplateCopyComponent} from 'src/app/shared/modals/template-copy/template-copy.component';

const GET_CONTENT_QUERY = gql`
  query TemplateDetails_GetContent($templateId: String, $templateItemId: String) {
    content(templateId: $templateId, templateItemId: $templateItemId) {
      totalCount
      edges {
        node {
          id
          templateItem {
            id
            title
            template {
              id
              title
              items {
                id
              }
            }
            labels {
              edges {
                node {
                  ...MultiSelectComponentLabelFragment
                }
              }
            }
            tags {
              edges {
                node {
                  ...MultiSelectComponentTagFragment
                }
              }
            }
          }
          type
          title
          views {
            totalCount
          }
          deleted
          deletedReason
        }
      }
    }
  }
  ${labelFrag}
  ${tagFrag}
`;

const getRole = gql`
    query TemplateDetailsComponent__getRole {
        me {
            id
            role
        }
    }
`;

@Component({
  selector: 'app-template-details',
  templateUrl: './template-details.component.html',
  styleUrls: ['./template-details.component.scss'],
  standalone: false,
})
export class TemplateDetailsComponent implements OnInit, OnDestroy {
  content: GetContent_content_edges_node[] = [];
  isMenuOpen = false;
  templateItem: TemplateService_GetTemplateItem_templateItem | null = null;
  labels: MultiSelectComponentLabelFragment[] = [];
  tags: MultiSelectComponentTagFragment[] = [];
  getUrl = getUrl;
  contentLoading = true;
  bsModalRef: BsModalRef | null = null;
  activeDropdown: string | null = null;
  ImageTypeEnum = ImageTypeEnum;
  TemplateEditMode = TemplateEditMode;
  editTemplateLabels$ = new Subject<void>();
  editTemplateTags$ = new Subject<void>();
  private subscriptions: Subscription[] = [];
  currentUser: TemplateDetailsComponent__getRole_me | undefined;
  RoleType = RoleType;

  constructor(
    private apollo: Apollo,
    private modalService: BsModalService,
    private navigation: NavigationService,
    private router: Router,
    private toastr: ToastrService,
    private errorHandler: ErrorService,
    private route: ActivatedRoute,
    private templateService: TemplateService,
  ) {}

  ngOnInit(): void {
    const templateItem$ = this.route.params.pipe(
      map(params => params.id as string),
      switchMap(templateItemId => this.templateService.getTemplateItem$(templateItemId)),
    );

    this.subscriptions.push(
      templateItem$.subscribe(templateItem => {
        this.templateItem = templateItem;
        this.labels = templateItem.labels.edges.map(e => e.node);
        this.tags = templateItem.tags.edges.map(e => e.node);
      }),

      this.apollo
        .watchQuery<TemplateDetailsComponent__getRole>({
          query: getRole,
        })
        .valueChanges.subscribe(({data}) => {
          this.currentUser = data?.me;
        }),

      templateItem$
        .pipe(
          switchMap(templateItem =>
            this.apollo
              .watchQuery<TemplateDetails_GetContent, TemplateDetails_GetContentVariables>({
                query: GET_CONTENT_QUERY,
                variables: {
                  templateId: templateItem.template.id,
                  templateItemId: templateItem.id,
                },
                notifyOnNetworkStatusChange: true,
              })
              .valueChanges.pipe(
                catchError(({error}) => {
                  this.errorHandler.handleDefault(error);
                  return EMPTY;
                }),
                map(response => ({
                  ...response,
                  content: (response.data?.content.edges ?? []).map(e => e.node),
                })),
              ),
          ),
        )
        .subscribe(({content, loading}) => {
          this.contentLoading = loading;
          this.content = content;
        }),

      fromEvent(window, 'scroll').subscribe(() => {
        this.activeDropdown = null;
        this.isMenuOpen = false;
      }),
    );
  }

  passwordProtect(templateItem: TemplateService_GetTemplateItem_templateItem): void {
    this.bsModalRef = this.modalService.show(PasswordProtectComponent, {
      class: 'password-protect-modal',
      initialState: {templateItem},
    });
  }

  editTemplateTitle(templateItem: TemplateService_GetTemplateItem_templateItem): void {
    this.bsModalRef = this.modalService.show(TemplateTitleComponent, {
      class: 'template-title-modal',
      initialState: {templateItem},
    });
    this.isMenuOpen = false;
  }

  copyTemplate(templateItem: TemplateService_GetTemplateItem_templateItem): void {
    this.bsModalRef = this.modalService.show(TemplateCopyComponent, {
      class: 'template-copy-modal',
      initialState: {templateItem},
    });
    this.isMenuOpen = false;
  }

  copyUrl(url: string): void {
    navigator.clipboard
      .writeText(url)
      .then(() => this.toastr.success($localize`URL copied to clipboard!`))
      .catch(() => this.toastr.error($localize`Failed to copy URL.`));
  }

  selectDropdown(id: string): void {
    if (this.activeDropdown === id) {
      this.activeDropdown = null;
      return;
    }
    this.activeDropdown = id;
  }

  editTemplate(templateItem: TemplateService_GetTemplateItem_templateItem): void {
    this.router.navigate(['templates', TemplateEditMode.EDIT, templateItem.id]);
  }

  deleteContent(contentId: string): void {
    this.bsModalRef = this.modalService.show(ContentDeleteComponent, {initialState: {contentId}});
    this.isMenuOpen = false;
  }

  deleteContentOfTemplateItem(templateItemId: string): void {
    this.bsModalRef = this.modalService.show(ContentDeleteComponent, {
      initialState: {templateItemId},
    });
    this.isMenuOpen = false;
  }

  archiveTemplate(templateItemId: string, isArchived: boolean): void {
    this.bsModalRef = this.modalService.show(ArchiveTemplateComponent, {
      class: 'archive-template-modal',
      initialState: {templateItemId, isArchived},
    });
    this.isMenuOpen = false;
  }

  toContentWithTemplateItemId(id: string): void {
    if (this.content.length === 0) {
      return;
    }
    this.router.navigate(['/content'], {queryParams: {templateItemId: id}});
  }

  backToTemplateOverview(): void {
    const {navigationId} = window.history.state;
    if (navigationId > 1) {
      window.history.back();
    } else {
      this.router.navigateByUrl('/templates');
    }
  }

  viewReport(id: string): void {
    this.router.navigate(['/reports'], {queryParams: {contentId: id}});
  }

  editLabels() {
    this.bsModalRef = this.modalService.show(EditTemplateLabelsComponent, {
      initialState: {
        templateItemId: this.templateItem?.id,
        currentLabels: this.templateItem?.labels.edges.map(e => e.node),
      },
    });
  }

  editTags() {
    this.bsModalRef = this.modalService.show(EditTemplateTagsComponent, {
      initialState: {
        templateItemId: this.templateItem?.id,
        currentTags: this.templateItem?.tags.edges.map(e => e.node),
      },
    });
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach(s => s.unsubscribe());
  }
}
